import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Fade,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Psychology as AIIcon,
  Groups as TeamIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  TrendingUp as TrendingIcon,
  TaskAlt as TaskIcon,
  AutoAwesome as AutoAwesomeIcon,
  Close as CloseIcon,
  Star as StarIcon,
  Bolt as BoltIcon,
  Rocket as RocketIcon,
  Verified as VerifiedIcon,
  Gavel as GavelIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import LandingPageRadarChart from '../components/LandingPageRadarChart';
import LandingPageKpiDashboard from '../components/LandingPageKpiDashboard';
import { mockAuditData, mockKpiDataLandingPage } from '../data/mockAuditData';

// ============================================================================
// LANDING PAGE COMPONENT
// Marketing/landing page with features, pricing, and login modal
// ============================================================================

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { login, isLoading, isAuthenticated, error: authError, clearError } = useAuth();
  
  // Login modal state
  const [loginOpen, setLoginOpen] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState<string>('');
  
  // Animation states
  const [heroVisible, setHeroVisible] = useState(false);
  const [featuresVisible, setFeaturesVisible] = useState(false);

  // Check if this is an invite redirect (Supabase sends tokens in hash)
  useEffect(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    
    // Check for invite tokens in hash (most common)
    if (hash && (hash.includes('access_token') || hash.includes('type=invite') || hash.includes('refresh_token'))) {
      console.log('🔗 Detected invite tokens in URL hash, redirecting to /accept-invite');
      console.log('🔗 Hash:', hash);
      navigate('/accept-invite' + hash, { replace: true });
      return;
    }
    
    // Check for invite tokens in query params (fallback)
    if (search && (search.includes('access_token') || search.includes('type=invite'))) {
      console.log('🔗 Detected invite tokens in URL search, redirecting to /accept-invite');
      console.log('🔗 Search:', search);
      navigate('/accept-invite' + search, { replace: true });
      return;
    }
  }, [navigate]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Trigger animations on mount
  useEffect(() => {
    setHeroVisible(true);
    const timer = setTimeout(() => setFeaturesVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  // Handle login form
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await login(formData.email, formData.password);
      navigate('/dashboard');
    } catch (error: any) {
      setError(error.message || 'Login failed. Please try again.');
    }
  };

  const openLogin = () => {
    clearError();
    setLoginOpen(true);
  };

  const closeLogin = () => {
    setLoginOpen(false);
    setFormData({ email: '', password: '' });
    setError('');
  };

  // ============================================================================
  // FEATURE DATA
  // ============================================================================
  const features = [
    {
      icon: <AIIcon sx={{ fontSize: 48 }} />,
      title: 'AI-Powered Templates',
      description: 'Generate comprehensive review templates in seconds with our intelligent AI builder. Just describe your review topic and watch the magic happen.',
      color: '#E53935',
    },
    {
      icon: <AssessmentIcon sx={{ fontSize: 48 }} />,
      title: 'Smart Analysis',
      description: 'Get instant AI-generated insights after every review. Identify high-performing areas, gaps, and actionable recommendations automatically.',
      color: '#43A047',
    },
    {
      icon: <TrendingIcon sx={{ fontSize: 48 }} />,
      title: 'KPI Tracking',
      description: 'Track KPIs over time with traffic light indicators. AI automatically suggests relevant KPIs and links them to reviews to measure real impact.',
      color: '#00BCD4',
    },
    {
      icon: <TaskIcon sx={{ fontSize: 48 }} />,
      title: 'Action Item Tracking',
      description: 'Transform review insights into trackable tasks with AI-generated sub-tasks. Never lose sight of what needs to be done.',
      color: '#1976D2',
    },
    {
      icon: <TeamIcon sx={{ fontSize: 48 }} />,
      title: 'Team Collaboration',
      description: 'Invite unlimited team members, assign tasks, and keep everyone aligned. Built for consulting teams of any size.',
      color: '#9C27B0',
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 48 }} />,
      title: 'Enterprise Security',
      description: 'Bank-level security with isolated data per organisation. GDPR and Australian Privacy Act ready. Your data stays protected.',
      color: '#FF9800',
    },
  ];

  // ============================================================================
  // PRICING DATA
  // ============================================================================
  const pricingPlans = [
    {
      name: 'Platform Subscription',
      price: '$49',
      period: '/month',
      description: 'Everything you need to run reviews',
      features: [
        'Unlimited team members',
        'Unlimited organisations',
        'Unlimited manual templates',
        '5 AI-generated templates/month',
        'Full action item management',
        'Dashboards & reporting',
        'Community template access',
      ],
      highlighted: false,
      annual: '$499/year (save 15%)',
    },
  ];

  const reviewPacks = [
    { name: 'Starter', reviews: 3, price: '$249', perReview: '$83' },
    { name: 'Professional', reviews: 10, price: '$699', perReview: '$69', popular: true },
    { name: 'Scale', reviews: 30, price: '$1,499', perReview: '$49' },
  ];

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0D0D0D 0%, #1A1A2E 50%, #16213E 100%)',
      overflow: 'hidden',
      // Custom scrollbar styling for entire page
      '& *::-webkit-scrollbar': {
        width: '12px',
        height: '12px',
      },
      '& *::-webkit-scrollbar-track': {
        background: 'rgba(255,255,255,0.03)',
        borderRadius: '10px',
      },
      '& *::-webkit-scrollbar-thumb': {
        background: 'linear-gradient(180deg, #E53935 0%, #C62828 100%)',
        borderRadius: '10px',
        border: '2px solid rgba(0,0,0,0.3)',
        '&:hover': {
          background: 'linear-gradient(180deg, #EF5350 0%, #E53935 100%)',
        },
      },
      // Firefox scrollbar styling
      scrollbarWidth: 'thin',
      scrollbarColor: '#E53935 rgba(255,255,255,0.03)',
    }}>
      {/* ====================================================================== */}
      {/* NAVIGATION BAR */}
      {/* ====================================================================== */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backdropFilter: 'blur(20px)',
          backgroundColor: 'rgba(13, 13, 13, 0.8)',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 2,
            }}
          >
            {/* Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                component="img"
                src="/logo.svg"
                alt="CHECK SET GO"
                sx={{ width: 40, height: 48 }}
              />
              <Box>
                <Typography
                  sx={{
                    fontFamily: '"Bebas Neue", "Oswald", sans-serif',
                    fontWeight: 400,
                    fontSize: '1.1rem',
                    lineHeight: 0.9,
                    color: '#FFFFFF',
                    letterSpacing: '2px',
                  }}
                >
                  CHECK SET GO
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.65rem',
                    color: '#E53935',
                    fontWeight: 600,
                    letterSpacing: '1.5px',
                    textTransform: 'uppercase',
                  }}
                >
                  Capability Platform
                </Typography>
              </Box>
            </Box>

            {/* Nav Links */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              {!isMobile && (
                <>
                  <Typography
                    component="a"
                    href="#features"
                    sx={{
                      color: 'rgba(255,255,255,0.7)',
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      '&:hover': { color: '#FFFFFF' },
                      transition: 'color 0.2s',
                    }}
                  >
                    Features
                  </Typography>
                  <Typography
                    component="a"
                    href="#security"
                    sx={{
                      color: 'rgba(255,255,255,0.7)',
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      '&:hover': { color: '#FFFFFF' },
                      transition: 'color 0.2s',
                    }}
                  >
                    Security
                  </Typography>
                  <Typography
                    component="a"
                    href="#pricing"
                    sx={{
                      color: 'rgba(255,255,255,0.7)',
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      '&:hover': { color: '#FFFFFF' },
                      transition: 'color 0.2s',
                    }}
                  >
                    Pricing
                  </Typography>
                </>
              )}
              <Button
                variant="contained"
                onClick={openLogin}
                sx={{
                  background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
                  color: '#FFFFFF',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  borderRadius: '50px',
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  boxShadow: '0 4px 20px rgba(229, 57, 53, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #F44336 0%, #D32F2F 100%)',
                    boxShadow: '0 6px 25px rgba(229, 57, 53, 0.5)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Sign In
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ====================================================================== */}
      {/* HERO SECTION */}
      {/* ====================================================================== */}
      <Box
        sx={{
          pt: { xs: 16, md: 20 },
          pb: { xs: 10, md: 16 },
          position: 'relative',
        }}
      >
        {/* Background Effects */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '10%',
            width: '500px',
            height: '500px',
            background: 'radial-gradient(circle, rgba(229, 57, 53, 0.15) 0%, transparent 70%)',
            filter: 'blur(60px)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            right: '5%',
            width: '400px',
            height: '400px',
            background: 'radial-gradient(circle, rgba(25, 118, 210, 0.1) 0%, transparent 70%)',
            filter: 'blur(60px)',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="lg">
          <Fade in={heroVisible} timeout={1000}>
            <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
              {/* Badge */}
              <Chip
                icon={<AutoAwesomeIcon sx={{ color: '#FFD700 !important', fontSize: 18 }} />}
                label="AI-Assisted Capability Platform"
                sx={{
                  mb: 4,
                  background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 193, 7, 0.1) 100%)',
                  border: '1px solid rgba(255, 215, 0, 0.3)',
                  color: '#FFD700',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  py: 2.5,
                  px: 1,
                }}
              />

              {/* Main Headline */}
              <Typography
                variant="h1"
                sx={{
                  fontFamily: '"Bebas Neue", "Oswald", sans-serif',
                  fontWeight: 400,
                  fontSize: { xs: '3rem', sm: '4.5rem', md: '6rem' },
                  lineHeight: 1,
                  color: '#FFFFFF',
                  letterSpacing: '3px',
                  mb: 2,
                }}
              >
                UNDERSTAND REALITY
              </Typography>
              <Typography
                variant="h1"
                sx={{
                  fontFamily: '"Bebas Neue", "Oswald", sans-serif',
                  fontWeight: 400,
                  fontSize: { xs: '3rem', sm: '4.5rem', md: '6rem' },
                  lineHeight: 1,
                  background: 'linear-gradient(135deg, #E53935 0%, #FF6F61 50%, #E53935 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '3px',
                  mb: 4,
                }}
              >
                EXECUTE VISIBLY
              </Typography>

              {/* Subheadline */}
              <Typography
                sx={{
                  fontSize: { xs: '1.1rem', md: '1.35rem' },
                  color: 'rgba(255,255,255,0.7)',
                  maxWidth: '700px',
                  mx: 'auto',
                  mb: 5,
                  lineHeight: 1.7,
                  fontWeight: 400,
                }}
              >
                A capability platform that helps organisations understand reality, decide what matters, 
                and execute improvement — visibly and repeatedly. Create AI-powered review templates, 
                all in one beautiful workspace.
              </Typography>

              {/* CTA Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/register')}
                  startIcon={<RocketIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    px: 5,
                    py: 2,
                    borderRadius: '50px',
                    fontSize: '1.1rem',
                    textTransform: 'none',
                    boxShadow: '0 8px 30px rgba(229, 57, 53, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #F44336 0%, #D32F2F 100%)',
                      boxShadow: '0 12px 40px rgba(229, 57, 53, 0.5)',
                      transform: 'translateY(-3px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Start Free Trial
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  href="#features"
                  sx={{
                    borderColor: 'rgba(255,255,255,0.3)',
                    color: '#FFFFFF',
                    fontWeight: 600,
                    px: 5,
                    py: 2,
                    borderRadius: '50px',
                    fontSize: '1.1rem',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: 'rgba(255,255,255,0.6)',
                      backgroundColor: 'rgba(255,255,255,0.05)',
                    },
                  }}
                >
                  Learn More
                </Button>
              </Box>

              {/* Trust Badges */}
              <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
                {[
                  { icon: <CheckIcon />, text: '14-day free trial' },
                  { icon: <CheckIcon />, text: 'No credit card required' },
                  { icon: <CheckIcon />, text: '1 free review credit' },
                ].map((badge, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ color: '#43A047', fontSize: 20 }}>{badge.icon}</Box>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
                      {badge.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Fade>

          {/* App Preview - Live Radar Chart Demo */}
          <Fade in={heroVisible} timeout={1200}>
            <Box
              sx={{
                mt: 8,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 1100,
                  background: '#000000',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 4,
                  p: { xs: 3, md: 4 },
                  boxShadow: '0 25px 50px rgba(0,0,0,0.8)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 30px 60px rgba(0,0,0,0.9)',
                  },
                }}
              >
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Typography
                    sx={{
                      fontFamily: '"Bebas Neue", "Oswald", sans-serif',
                      fontSize: '2rem',
                      fontWeight: 400,
                      letterSpacing: '2px',
                      background: 'linear-gradient(135deg, #FFFFFF 0%, rgba(255,255,255,0.8) 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 1,
                    }}
                  >
                    {mockAuditData.audit_name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                    <Typography
                      sx={{
                        color: 'rgba(255,255,255,0.6)',
                        fontSize: '1rem',
                      }}
                    >
                      {mockAuditData.company_name}
                    </Typography>
                    <Box
                      sx={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255,255,255,0.3)',
                      }}
                    />
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 0.5,
                        borderRadius: '20px',
                        background: 'linear-gradient(135deg, rgba(67, 160, 71, 0.2) 0%, rgba(67, 160, 71, 0.1) 100%)',
                        border: '1px solid rgba(67, 160, 71, 0.3)',
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: '"Bebas Neue", "Oswald", sans-serif',
                          fontSize: '1.3rem',
                          color: '#43A047',
                          fontWeight: 700,
                        }}
                      >
                        {mockAuditData.overall_score.toFixed(1)}%
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: 'rgba(255,255,255,0.6)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Overall Score
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box 
                  sx={{ 
                    minHeight: 500,
                    '& .MuiPaper-root': {
                      backgroundColor: '#000000 !important',
                      color: '#FFFFFF',
                      boxShadow: 'none !important',
                    },
                    '& .MuiTypography-root': {
                      color: '#FFFFFF',
                    },
                    // Style the detail panel header
                    '& .MuiBox-root > .MuiBox-root > .MuiTypography-h6': {
                      fontSize: '1.5rem',
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #FFFFFF 0%, rgba(255,255,255,0.7) 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    },
                    // Style the score display
                    '& .MuiBox-root > .MuiBox-root > .MuiBox-root > .MuiTypography-h4': {
                      fontFamily: '"Bebas Neue", "Oswald", sans-serif',
                      fontSize: '3.5rem',
                      textShadow: '0 0 20px currentColor',
                    },
                    // Style the table
                    '& .MuiTableContainer-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                    },
                    '& .MuiTableCell-root': {
                      color: '#FFFFFF',
                      borderColor: 'rgba(255, 255, 255, 0.05)',
                      fontSize: '0.9rem',
                      padding: '18px',
                      lineHeight: 1.6,
                    },
                    '& .MuiTableHead-root .MuiTableCell-root': {
                      backgroundColor: 'rgba(229, 57, 53, 0.1)',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      color: 'rgba(255, 255, 255, 0.9)',
                      borderBottom: '2px solid rgba(229, 57, 53, 0.3)',
                    },
                    '& .MuiTableBody-root .MuiTableRow-root': {
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        transform: 'translateX(4px)',
                      },
                    },
                    // Style score cells with badges
                    '& .MuiTableBody-root .MuiTableCell-root:last-child': {
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      fontFamily: '"Bebas Neue", "Oswald", sans-serif',
                    },
                    // Instruction text styling
                    '& .MuiBox-root > .MuiBox-root > .MuiTypography-body1': {
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontStyle: 'italic',
                      fontSize: '1rem',
                    },
                  }}
                >
                  <LandingPageRadarChart
                    data={{
                      labels: mockAuditData.category_scores.map(cat => cat.name),
                      datasets: [
                        {
                          label: `Current (${new Date(mockAuditData.analysis_date).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })})`,
                          data: mockAuditData.category_scores.map(cat => cat.score),
                          backgroundColor: 'rgba(229, 57, 53, 0.2)',
                          borderColor: '#E53935',
                          borderWidth: 3,
                          pointBackgroundColor: '#E53935',
                          pointBorderColor: '#fff',
                          pointHoverBackgroundColor: '#fff',
                          pointHoverBorderColor: '#E53935',
                        },
                        ...(mockAuditData.prior_year_category_scores && mockAuditData.prior_year_analysis_date ? [{
                          label: `Prior Year (${new Date(mockAuditData.prior_year_analysis_date).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })})`,
                          data: mockAuditData.prior_year_category_scores.map(cat => cat.score),
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                          borderColor: 'rgba(255, 255, 255, 0.4)',
                          borderWidth: 2,
                          pointBackgroundColor: 'rgba(255, 255, 255, 0.4)',
                          pointBorderColor: '#fff',
                          pointHoverBackgroundColor: '#fff',
                          pointHoverBorderColor: 'rgba(255, 255, 255, 0.4)',
                        }] : []),
                      ],
                    }}
                    detailedResults={mockAuditData.detailed_results}
                    priorYearDetailedResults={mockAuditData.prior_year_detailed_results}
                  />
                </Box>
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    background: 'rgba(67, 160, 71, 0.1)',
                    border: '1px solid rgba(67, 160, 71, 0.3)',
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    sx={{
                      textAlign: 'center',
                      color: 'rgba(255,255,255,0.9)',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                    }}
                  >
                    ✨ Interactive Demo • Try the Drill-Down!
                  </Typography>
                  <Typography
                    sx={{
                      textAlign: 'center',
                      color: 'rgba(255,255,255,0.6)',
                      fontSize: '0.85rem',
                      mt: 0.5,
                    }}
                  >
                    Click any category label to see detailed questions and scores • Sign up for AI insights & action item generation
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* ====================================================================== */}
      {/* FEATURES SECTION */}
      {/* ====================================================================== */}
      <Box
        id="features"
        sx={{
          py: { xs: 10, md: 16 },
          background: 'linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.02) 50%, transparent 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Fade in={featuresVisible} timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Chip
                label="FEATURES"
                sx={{
                  mb: 3,
                  background: 'rgba(229, 57, 53, 0.1)',
                  border: '1px solid rgba(229, 57, 53, 0.3)',
                  color: '#E53935',
                  fontWeight: 700,
                  letterSpacing: '2px',
                }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontFamily: '"Bebas Neue", "Oswald", sans-serif',
                  fontWeight: 400,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  color: '#FFFFFF',
                  letterSpacing: '2px',
                  mb: 2,
                }}
              >
                EVERYTHING YOU NEED
              </Typography>
              <Typography
                sx={{
                  fontSize: '1.1rem',
                  color: 'rgba(255,255,255,0.6)',
                  maxWidth: '600px',
                  mx: 'auto',
                }}
              >
                Powerful tools designed specifically for consultants who want to
                deliver exceptional review experiences to their organisations.
              </Typography>
            </Box>
          </Fade>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 4,
            }}
          >
            {features.map((feature, index) => (
              <Card
                key={index}
                sx={{
                  background: 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 4,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 20px 40px rgba(0,0,0,0.3), 0 0 30px ${feature.color}20`,
                    borderColor: `${feature.color}40`,
                  },
                }}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${feature.color}20 0%, ${feature.color}10 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                      color: feature.color,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#FFFFFF',
                      mb: 2,
                      fontSize: '1.25rem',
                    }}
                  >
                    {feature.title}
                  </Typography>
                  

                  <Typography
                    sx={{
                      color: 'rgba(255,255,255,0.6)',
                      lineHeight: 1.7,
                    }}
                  >
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ====================================================================== */}
      {/* HOW IT WORKS SECTION */}
      {/* ====================================================================== */}
      <Box sx={{ py: { xs: 10, md: 16 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label="HOW IT WORKS"
              sx={{
                mb: 3,
                background: 'rgba(25, 118, 210, 0.1)',
                border: '1px solid rgba(25, 118, 210, 0.3)',
                color: '#1976D2',
                fontWeight: 700,
                letterSpacing: '2px',
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontFamily: '"Bebas Neue", "Oswald", sans-serif',
                fontWeight: 400,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                color: '#FFFFFF',
                letterSpacing: '2px',
              }}
            >
              CHECK → SET → GO
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: 4,
            }}
          >
            {[
              {
                step: '01',
                title: 'CHECK',
                description: 'Understand reality with structured reviews that surface gaps, drift, and missing capability.',
                icon: <AssessmentIcon sx={{ fontSize: 28 }} />,
                // [OPTIONAL: Add small screenshot thumbnail - Review overview - 250x150px]
              },
              {
                step: '02',
                title: 'SET',
                description: 'Decide what matters now. Make trade-offs explicit, assign ownership, and define success.',
                icon: <VerifiedIcon sx={{ fontSize: 28 }} />,
                // [OPTIONAL: Add small screenshot thumbnail - Prioritisation & ownership - 250x150px]
              },
              {
                step: '03',
                title: 'GO',
                description: 'Execute visibly with owned actions, evidence capture, and progress tracking.',
                icon: <RocketIcon sx={{ fontSize: 28 }} />,
                // [OPTIONAL: Add small screenshot thumbnail - Action execution & evidence - 250x150px]
              },
              {
                step: '04',
                title: 'PROGRESS REVIEW',
                description: 'Repeat the cycle with proof. Track what changed since the last improvement cycle.',
                icon: <TrendingIcon sx={{ fontSize: 28 }} />,
                // [OPTIONAL: Add small screenshot thumbnail - Progress review comparison - 250x150px]
              },
            ].map((item, index) => (
              <Box
                key={index}
                sx={{
                  textAlign: 'center',
                  position: 'relative',
                  p: 3,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Bebas Neue", "Oswald", sans-serif',
                    fontSize: '5rem',
                    fontWeight: 400,
                    color: 'rgba(229, 57, 53, 0.08)',
                    position: 'absolute',
                    top: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 0,
                  }}
                >
                  {item.step}
                </Typography>
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    pt: 3,
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2.5,
                      color: '#FFFFFF',
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#FFFFFF',
                      mb: 1.5,
                      fontSize: '1.1rem',
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'rgba(255,255,255,0.6)',
                      lineHeight: 1.6,
                      fontSize: '0.95rem',
                    }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ====================================================================== */}
      {/* KPI FEATURE SPOTLIGHT */}
      {/* ====================================================================== */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          background: 'linear-gradient(180deg, rgba(0, 188, 212, 0.05) 0%, transparent 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              gap: 6,
            }}
          >
            {/* Left: Text Content */}
            <Box sx={{ flex: 1 }}>
              <Chip
                icon={<TrendingIcon sx={{ color: '#00BCD4 !important', fontSize: 18 }} />}
                label="MEASURE REAL IMPACT"
                sx={{
                  mb: 3,
                  background: 'rgba(0, 188, 212, 0.1)',
                  border: '1px solid rgba(0, 188, 212, 0.3)',
                  color: '#00BCD4',
                  fontWeight: 700,
                  letterSpacing: '2px',
                }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontFamily: '"Bebas Neue", "Oswald", sans-serif',
                  fontWeight: 400,
                  fontSize: { xs: '2.2rem', md: '3rem' },
                  color: '#FFFFFF',
                  letterSpacing: '2px',
                  mb: 3,
                  lineHeight: 1.2,
                }}
              >
                PROVE YOUR VALUE WITH KPI TRACKING
              </Typography>
              <Typography
                sx={{
                  fontSize: '1.1rem',
                  color: 'rgba(255,255,255,0.7)',
                  mb: 4,
                  lineHeight: 1.7,
                }}
              >
                Stop guessing if your reviews are making a difference. Track key performance 
                indicators over time with traffic light dashboards that show real business improvements.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {[
                  { icon: <AIIcon sx={{ fontSize: 28, color: '#00BCD4' }} />, text: 'AI suggests 10 relevant KPIs based on industry and organisation profile' },
                  { icon: <TrendingIcon sx={{ fontSize: 28, color: '#00BCD4' }} />, text: 'Automatically links reviews to KPIs they influence' },
                  { icon: <CheckIcon sx={{ fontSize: 28, color: '#4caf50' }} />, text: 'Traffic light indicators show performance at a glance' },
                  { icon: <AssessmentIcon sx={{ fontSize: 28, color: '#00BCD4' }} />, text: 'Track before/after values to demonstrate measurable impact' },
                  { icon: <TaskIcon sx={{ fontSize: 28, color: '#00BCD4' }} />, text: 'Export KPI data to CSV for reports' },
                ].map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', pt: 0.5 }}>
                      {item.icon}
                    </Box>
                    <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '1rem', pt: 0.5 }}>
                      {item.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Right: Interactive KPI Dashboard */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
              }}
            >
              <Box
                sx={{
                  p: 3,
                  background: '#000000',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 3,
                  boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
                  },
                  // Override styles for better visibility
                  '& .MuiChip-root': {
                    fontSize: '0.8rem',
                  },
                  '& .MuiTypography-root': {
                    color: '#FFFFFF',
                  },
                }}
              >
                <LandingPageKpiDashboard kpis={mockKpiDataLandingPage} />
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ====================================================================== */}
      {/* RESULTS & BENEFITS SECTION */}
      {/* ====================================================================== */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          background: 'linear-gradient(180deg, transparent 0%, rgba(67, 160, 71, 0.05) 50%, transparent 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label="RESULTS YOU'LL GET"
              sx={{
                mb: 3,
                background: 'rgba(67, 160, 71, 0.1)',
                border: '1px solid rgba(67, 160, 71, 0.3)',
                color: '#43A047',
                fontWeight: 700,
                letterSpacing: '2px',
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontFamily: '"Bebas Neue", "Oswald", sans-serif',
                fontWeight: 400,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                color: '#FFFFFF',
                letterSpacing: '2px',
                mb: 2,
              }}
            >
              DELIVER MORE VALUE TO ORGANISATIONS
            </Typography>
            <Typography
              sx={{
                fontSize: '1.1rem',
                color: 'rgba(255,255,255,0.6)',
                maxWidth: '700px',
                mx: 'auto',
              }}
            >
              Stop juggling spreadsheets and email threads. Deliver professional reviews 
              that organisations love and track real business improvements.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(3, 1fr)',
              },
              gap: 4,
            }}
          >
            {[
              {
                icon: '⚡',
                title: 'Complete Reviews 5x Faster',
                description: 'AI generates comprehensive templates in seconds. Spend less time on admin, more time adding value.',
              },
              {
                icon: '📊',
                title: 'Prove Your Impact with Data',
                description: 'Track organisation KPIs before and after reviews. Show measurable improvements that justify your fees.',
              },
              {
                icon: '✨',
                title: 'Impress with Professional Reports',
                description: 'AI-powered analysis and insights make every review report look polished and actionable.',
              },
              {
                icon: '🎯',
                title: 'Never Miss a Follow-up',
                description: 'Action items automatically tracked with AI-generated sub-tasks. Ensure success every time.',
              },
              {
                icon: '🤝',
                title: 'Collaborate Seamlessly',
                description: 'Your entire team stays aligned. Organisations can track progress in real-time with their own login.',
              },
              {
                icon: '🚀',
                title: 'Scale Your Practice',
                description: 'Handle more organisations without hiring more staff. Templates, workflows, and automation built-in.',
              },
            ].map((item, index) => (
              <Box
                key={index}
                sx={{
                  p: 4,
                  background: 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 3,
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    borderColor: 'rgba(67, 160, 71, 0.3)',
                    boxShadow: '0 15px 40px rgba(0,0,0,0.3)',
                  },
                }}
              >
                <Typography sx={{ fontSize: '3rem', mb: 2 }}>
                  {item.icon}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#FFFFFF',
                    mb: 2,
                    fontSize: '1.25rem',
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  sx={{
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '1rem',
                    lineHeight: 1.7,
                  }}
                >
                  {item.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ====================================================================== */}
      {/* TRUST & SECURITY SECTION (CONDENSED) */}
      {/* ====================================================================== */}
      <Box
        id="security"
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(180deg, transparent 0%, rgba(25, 118, 210, 0.03) 50%, transparent 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              icon={<SecurityIcon sx={{ color: '#1976D2 !important', fontSize: 18 }} />}
              label="ENTERPRISE SECURITY"
              sx={{
                mb: 3,
                background: 'rgba(25, 118, 210, 0.1)',
                border: '1px solid rgba(25, 118, 210, 0.3)',
                color: '#1976D2',
                fontWeight: 700,
                letterSpacing: '2px',
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontFamily: '"Bebas Neue", "Oswald", sans-serif',
                fontWeight: 400,
                fontSize: { xs: '2rem', md: '2.8rem' },
                color: '#FFFFFF',
                letterSpacing: '2px',
                mb: 2,
              }}
            >
              ENTERPRISE SECURITY. SIMPLE TO USE.
            </Typography>
            <Typography
              sx={{
                fontSize: '1rem',
                color: 'rgba(255,255,255,0.6)',
                maxWidth: '650px',
                mx: 'auto',
              }}
            >
              Bank-level security built in. GDPR and Australian Privacy Act ready. 
              Your organisation data stays isolated and protected.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: 3,
              mb: 6,
            }}
          >
            {[
              {
                icon: '🔐',
                title: 'Multi-Tenant Isolation',
                description: 'Each organisation\'s data completely isolated and secure.',
              },
              {
                icon: '🛡️',
                title: 'End-to-End Encryption',
                description: 'Bank-grade encryption protects data at rest and in transit.',
              },
              {
                icon: '🇦🇺',
                title: 'Privacy Act Ready',
                description: 'Australian Privacy Principles built-in.',
              },
              {
                icon: '🇪🇺',
                title: 'GDPR Ready',
                description: 'European data protection standards ready.',
              },
            ].map((item, index) => (
              <Box
                key={index}
                sx={{
                  p: 3,
                  background: 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 3,
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'rgba(25, 118, 210, 0.3)',
                    transform: 'translateY(-3px)',
                  },
                }}
              >
                <Typography sx={{ fontSize: '2.5rem', mb: 1.5 }}>
                  {item.icon}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: '#FFFFFF',
                    mb: 1,
                    fontSize: '0.95rem',
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  sx={{
                    color: 'rgba(255,255,255,0.5)',
                    fontSize: '0.85rem',
                    lineHeight: 1.5,
                  }}
                >
                  {item.description}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Legal Links */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', mb: 2, fontSize: '0.9rem' }}>
              Read our complete policies:
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
              {[
                { label: 'Privacy Policy', href: '/privacy-policy' },
                { label: 'Terms of Service', href: '/terms-of-service' },
                { label: 'Cookie Policy', href: '/cookie-policy' },
              ].map((link, index) => (
                <Typography
                  key={index}
                  component="a"
                  href={link.href}
                  sx={{
                    color: '#1976D2',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {link.label}
                </Typography>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ====================================================================== */}
      {/* PRICING SECTION */}
      {/* ====================================================================== */}
      <Box
        id="pricing"
        sx={{
          py: { xs: 10, md: 16 },
          background: 'linear-gradient(180deg, transparent 0%, rgba(229, 57, 53, 0.03) 50%, transparent 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label="PRICING"
              sx={{
                mb: 3,
                background: 'rgba(67, 160, 71, 0.1)',
                border: '1px solid rgba(67, 160, 71, 0.3)',
                color: '#43A047',
                fontWeight: 700,
                letterSpacing: '2px',
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontFamily: '"Bebas Neue", "Oswald", sans-serif',
                fontWeight: 400,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                color: '#FFFFFF',
                letterSpacing: '2px',
                mb: 2,
              }}
            >
              SIMPLE, PREDICTABLE PRICING
            </Typography>
            <Typography
              sx={{
                fontSize: '1.1rem',
                color: 'rgba(255,255,255,0.6)',
                maxWidth: '600px',
                mx: 'auto',
              }}
            >
              No surprise bills, no AI token tracking. Pay for what you use with 
              all AI features included in every review.
            </Typography>
          </Box>

          {/* Platform Subscription */}
          <Box sx={{ mb: 8 }}>
            <Card
              sx={{
                maxWidth: 500,
                mx: 'auto',
                background: 'linear-gradient(145deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(229, 57, 53, 0.3)',
                borderRadius: 4,
                overflow: 'visible',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                }}
              >
                <Chip
                  icon={<StarIcon sx={{ color: '#FFD700 !important', fontSize: 16 }} />}
                  label="BASE SUBSCRIPTION"
                  sx={{
                    background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    px: 2,
                  }}
                />
              </Box>
              <CardContent sx={{ p: 5, textAlign: 'center' }}>
                <Typography
                  sx={{
                    fontFamily: '"Bebas Neue", "Oswald", sans-serif',
                    fontSize: '4rem',
                    color: '#FFFFFF',
                    lineHeight: 1,
                  }}
                >
                  $49
                  <Typography
                    component="span"
                    sx={{ fontSize: '1.5rem', color: 'rgba(255,255,255,0.5)' }}
                  >
                    /month
                  </Typography>
                </Typography>
                <Typography
                  sx={{
                    color: 'rgba(255,255,255,0.5)',
                    mb: 4,
                    fontSize: '0.9rem',
                  }}
                >
                  or $499/year (save 15%)
                </Typography>
                <Box sx={{ textAlign: 'left' }}>
                  {pricingPlans[0].features.map((feature, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                      <CheckIcon sx={{ color: '#43A047', fontSize: 20 }} />
                      <Typography sx={{ color: 'rgba(255,255,255,0.8)' }}>{feature}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Review Packs */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h4"
              sx={{
                fontFamily: '"Bebas Neue", "Oswald", sans-serif',
                fontWeight: 400,
                fontSize: { xs: '1.8rem', md: '2.2rem' },
                color: '#FFFFFF',
                letterSpacing: '2px',
                mb: 1,
              }}
            >
              REVIEW CREDIT PACKS
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', mb: 4 }}>
              All AI analysis, commentary & sub-tasks included. Credits never expire.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 3,
              maxWidth: 900,
              mx: 'auto',
            }}
          >
            {reviewPacks.map((pack, index) => (
              <Card
                key={index}
                sx={{
                  background: pack.popular
                    ? 'linear-gradient(145deg, rgba(229, 57, 53, 0.15) 0%, rgba(229, 57, 53, 0.05) 100%)'
                    : 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: pack.popular
                    ? '2px solid rgba(229, 57, 53, 0.5)'
                    : '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 4,
                  position: 'relative',
                  overflow: 'visible',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                  },
                }}
              >
                {pack.popular && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                    }}
                  >
                    <Chip
                      icon={<BoltIcon sx={{ color: '#FFFFFF !important', fontSize: 16 }} />}
                      label="MOST POPULAR"
                      sx={{
                        background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
                        color: '#FFFFFF',
                        fontWeight: 700,
                      }}
                    />
                  </Box>
                )}
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: '#FFFFFF',
                      fontSize: '1.2rem',
                      mb: 2,
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                    }}
                  >
                    {pack.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Bebas Neue", "Oswald", sans-serif',
                      fontSize: '3rem',
                      color: '#FFFFFF',
                      lineHeight: 1,
                      mb: 1,
                    }}
                  >
                    {pack.price}
                  </Typography>
                  <Typography
                    sx={{
                      color: pack.popular ? '#E53935' : 'rgba(255,255,255,0.5)',
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    {pack.reviews} reviews
                  </Typography>
                  <Chip
                    label={`${pack.perReview} per review`}
                    sx={{
                      background: 'rgba(67, 160, 71, 0.1)',
                      border: '1px solid rgba(67, 160, 71, 0.3)',
                      color: '#43A047',
                      fontWeight: 600,
                    }}
                  />
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Organisation Collaboration Note */}
          <Box
            sx={{
              mt: 6,
              p: 4,
              background: 'linear-gradient(145deg, rgba(67, 160, 71, 0.1) 0%, rgba(67, 160, 71, 0.03) 100%)',
              border: '1px solid rgba(67, 160, 71, 0.2)',
              borderRadius: 4,
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                color: '#FFFFFF',
                fontSize: '1.1rem',
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <Box component="span" sx={{ fontSize: '1.5rem' }}>✨</Box>
              Organisation Collaboration Included
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Give your organisations their own login to view progress, action items, and collaborate on tasks. 
              No extra per-organisation fees — it's all included in your subscription.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ====================================================================== */}
      {/* CTA SECTION */}
      {/* ====================================================================== */}
      <Box sx={{ py: { xs: 10, md: 16 } }}>
        <Container maxWidth="md">
          <Box
            sx={{
              textAlign: 'center',
              p: { xs: 4, md: 8 },
              background: 'linear-gradient(145deg, rgba(229, 57, 53, 0.1) 0%, rgba(229, 57, 53, 0.05) 100%)',
              border: '1px solid rgba(229, 57, 53, 0.2)',
              borderRadius: 6,
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontFamily: '"Bebas Neue", "Oswald", sans-serif',
                fontWeight: 400,
                fontSize: { xs: '2rem', md: '3rem' },
                color: '#FFFFFF',
                letterSpacing: '2px',
                mb: 2,
              }}
            >
              READY TO BUILD REAL CAPABILITY?
            </Typography>
            <Typography
              sx={{
                fontSize: '1.1rem',
                color: 'rgba(255,255,255,0.6)',
                mb: 4,
                maxWidth: '500px',
                mx: 'auto',
              }}
            >
              Start your 14-day free trial today. No credit card required. 
              Get 1 free review credit to experience the full workflow.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              startIcon={<RocketIcon />}
              sx={{
                background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
                color: '#FFFFFF',
                fontWeight: 700,
                px: 6,
                py: 2,
                borderRadius: '50px',
                fontSize: '1.1rem',
                textTransform: 'none',
                boxShadow: '0 8px 30px rgba(229, 57, 53, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #F44336 0%, #D32F2F 100%)',
                  boxShadow: '0 12px 40px rgba(229, 57, 53, 0.5)',
                  transform: 'translateY(-3px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Start Free Trial
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ====================================================================== */}
      {/* FOOTER */}
      {/* ====================================================================== */}
      <Box
        sx={{
          py: 6,
          borderTop: '1px solid rgba(255,255,255,0.1)',
          background: 'rgba(0,0,0,0.3)',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 3,
            }}
          >
            {/* Logo & Tagline */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                component="img"
                src="/logo.svg"
                alt="CHECK SET GO"
                sx={{ width: 32, height: 38 }}
              />
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>
                CHECK SET GO — Capability Platform
              </Typography>
            </Box>

            {/* Legal Links */}
            <Box sx={{ display: 'flex', gap: { xs: 2, md: 4 }, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Typography
                component="a"
                href="/privacy-policy"
                sx={{
                  color: 'rgba(255,255,255,0.5)',
                  textDecoration: 'none',
                  fontSize: '0.85rem',
                  '&:hover': { color: '#FFFFFF' },
                  transition: 'color 0.2s',
                }}
              >
                Privacy Policy
              </Typography>
              <Typography
                component="a"
                href="/terms-of-service"
                sx={{
                  color: 'rgba(255,255,255,0.5)',
                  textDecoration: 'none',
                  fontSize: '0.85rem',
                  '&:hover': { color: '#FFFFFF' },
                  transition: 'color 0.2s',
                }}
              >
                Terms of Service
              </Typography>
              <Typography
                component="a"
                href="/cookie-policy"
                sx={{
                  color: 'rgba(255,255,255,0.5)',
                  textDecoration: 'none',
                  fontSize: '0.85rem',
                  '&:hover': { color: '#FFFFFF' },
                  transition: 'color 0.2s',
                }}
              >
                Cookie Policy
              </Typography>
            </Box>

            {/* Copyright */}
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>
              © {new Date().getFullYear()} Centre Of Business. All rights reserved.
            </Typography>
          </Box>

          {/* Security Badges */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 3,
              mt: 4,
              pt: 3,
              borderTop: '1px solid rgba(255,255,255,0.05)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.2rem' }}>🇦🇺</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                Privacy Act Ready
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.2rem' }}>🇪🇺</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                GDPR Ready
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.2rem' }}>🔒</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                SOC 2 Infrastructure
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ====================================================================== */}
      {/* LOGIN MODAL */}
      {/* ====================================================================== */}
      <Dialog
        open={loginOpen}
        onClose={closeLogin}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(145deg, #1A1A2E 0%, #16213E 100%)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 4,
            overflow: 'hidden',
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ position: 'relative', p: 4 }}>
            <IconButton
              onClick={closeLogin}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'rgba(255,255,255,0.5)',
                '&:hover': { color: '#FFFFFF' },
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Logo */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                component="img"
                src="/logo.svg"
                alt="CHECK SET GO"
                sx={{ width: 60, height: 72, mb: 2 }}
              />
              <Typography
                sx={{
                  fontFamily: '"Bebas Neue", "Oswald", sans-serif',
                  fontWeight: 400,
                  fontSize: '1.5rem',
                  color: '#FFFFFF',
                  letterSpacing: '2px',
                }}
              >
                SIGN IN
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.85rem',
                  color: '#E53935',
                  fontWeight: 600,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                }}
              >
                Welcome Back
              </Typography>
            </Box>

            {(error || authError) && (
              <Alert severity="error" sx={{ mb: 3 }} data-testid="login-error">
                {error || authError}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} data-testid="login-form">
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                value={formData.email}
                onChange={handleChange}
                disabled={isLoading}
                data-testid="email-input"
                inputProps={{ 'data-testid': 'email-field' }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&.Mui-focused fieldset': { borderColor: '#E53935' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                  '& .MuiOutlinedInput-input': { color: '#FFFFFF' },
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                disabled={isLoading}
                data-testid="password-input"
                inputProps={{ 'data-testid': 'password-field' }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&.Mui-focused fieldset': { borderColor: '#E53935' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                  '& .MuiOutlinedInput-input': { color: '#FFFFFF' },
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                data-testid="login-submit"
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
                  fontWeight: 700,
                  borderRadius: '50px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #F44336 0%, #D32F2F 100%)',
                  },
                }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
              </Button>
              
              {/* Forgot Password Link */}
              <Box sx={{ textAlign: 'center', mb: 1 }}>
                <Typography
                  component="a"
                  href="/forgot-password"
                  onClick={(e) => {
                    e.preventDefault();
                    setLoginOpen(false);
                    navigate('/forgot-password');
                  }}
                  sx={{
                    color: 'rgba(255,255,255,0.7)',
                    fontSize: '0.85rem',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    '&:hover': { 
                      textDecoration: 'underline',
                      color: '#E53935' 
                    },
                  }}
                >
                  Forgot your password?
                </Typography>
              </Box>

              {/* Sign Up Link */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  component="span"
                  sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}
                >
                  Don't have an account?{' '}
                </Typography>
                <Typography
                  component="a"
                  href="/register"
                  sx={{
                    color: '#E53935',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Sign Up
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LandingPage;

